import { useEffect, useMemo, useRef } from 'react';
import { MessagesSquare, Bot, Sparkles, Database, Cpu } from 'lucide-react';
import {
  Badge,
  PageHeader,
  SegmentedControl,
  Select,
  Spinner,
  type SelectOption,
} from '../../ui';
import { useChat } from '../../features/chat/useChat';
import { CHAT_MODELS, modelLabel } from '../../features/chat/chatModel';
import { ConversationList } from './ConversationList';
import { MessageBubble } from './MessageBubble';
import { Composer } from './Composer';
import { CitationsPanel } from './CitationsPanel';
import { IngestionPanel } from './IngestionPanel';

/**
 * Chat — a modern dark architecture chat space. Engineers converse against the
 * team knowledge base (REAL Bedrock RAG via api.knowledgeBase.retrieveAndGenerate)
 * with inline citations, or fall back to a plain-LLM conversation
 * (api.inference.converse). The grounded knowledge base is resolved server-side
 * from service configuration, and an ingestion panel uploads documents /
 * registers links.
 * Conversations are saved and reloadable through
 * api.documents<Conversation>('conversations').
 *
 * Strict layering: this page reaches data ONLY through the useChat hook.
 */
export function ChatPage() {
  const {
    conversations,
    active,
    modelId,
    loading,
    loadingActive,
    sending,
    newConversation,
    select,
    send,
    setModelId,
    remove,
    grounded,
    setGrounded,
    tenantKbs,
    selectedKbKey,
    kbActive,
    setKbKey,
    ingesting,
    ingestStatus,
    ingestFile,
  } = useChat();

  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const messages = active?.messages ?? [];

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length, sending, active?.id]);

  const modelOptions = useMemo<SelectOption[]>(
    () => CHAT_MODELS.map((m) => ({ value: m.modelId, label: m.label })),
    [],
  );

  const kbOptions = useMemo<SelectOption[]>(
    () =>
      tenantKbs.map((k) => ({
        value: k.bundleKey,
        label: k.bundleKey || k.knowledgeBaseId,
      })),
    [tenantKbs],
  );
  const hasKbs = tenantKbs.length > 0;

  return (
    <div>
      <PageHeader
        icon={<MessagesSquare className="h-5 w-5" />}
        title="Chat"
        description="Ask the team knowledge base with citations, or switch to a plain LLM chat."
        actions={
          <div className="flex items-center gap-2">
            {/* Grounding toggle */}
            <SegmentedControl
              size="sm"
              aria-label="Answer grounding"
              value={grounded ? 'kb' : 'plain'}
              onChange={(id) => setGrounded(id === 'kb')}
              items={[
                {
                  id: 'kb',
                  label: 'KB grounded',
                  icon: <Database className="h-3.5 w-3.5" />,
                },
                {
                  id: 'plain',
                  label: 'Plain LLM',
                  icon: <Cpu className="h-3.5 w-3.5" />,
                },
              ]}
            />
            {/* KB picker (grounded only): choose which registered KB grounds
                the reply. Disabled empty state when the tenant has none. */}
            {grounded &&
              (hasKbs ? (
                <div className="w-56">
                  <Select
                    aria-label="Knowledge base"
                    value={selectedKbKey ?? ''}
                    options={kbOptions}
                    onChange={(e) => setKbKey(e.target.value)}
                  />
                </div>
              ) : (
                <div className="w-56">
                  <Select
                    aria-label="Knowledge base"
                    value=""
                    options={[]}
                    placeholder="No knowledge bases"
                    disabled
                  />
                </div>
              ))}
            {/* Model selector (plain LLM only; grounded uses the selected KB) */}
            {!grounded && (
              <div className="w-56">
                <Select
                  aria-label="Model"
                  value={modelId}
                  options={modelOptions}
                  onChange={(e) => setModelId(e.target.value)}
                />
              </div>
            )}
          </div>
        }
      />

      <div className="flex h-[calc(100vh-13rem)] gap-4">
        <div className="flex w-72 shrink-0 flex-col gap-4 overflow-y-auto">
          <ConversationList
            conversations={conversations}
            activeId={active?.id ?? null}
            loading={loading}
            onNew={newConversation}
            onSelect={(id) => void select(id)}
            onRemove={(id) => void remove(id)}
          />
          <IngestionPanel
            ingesting={ingesting}
            ingestStatus={ingestStatus}
            onIngestFile={ingestFile}
          />
        </div>

        <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-2xl border border-surface-border bg-surface-1">
          {/* Conversation header */}
          <div className="flex items-center justify-between gap-3 border-b border-surface-border px-4 py-3">
            <div className="flex min-w-0 items-center gap-2">
              <Bot className="h-4 w-4 shrink-0 text-accent" />
              <span className="truncate text-sm font-medium text-zinc-200">
                {active?.title ?? 'New conversation'}
              </span>
            </div>
            <Badge tone="accent" dot>
              {kbActive ? 'KB grounded' : modelLabel(modelId)}
            </Badge>
          </div>

          {/* Message area */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5">
            {loadingActive ? (
              <div className="flex h-full items-center justify-center">
                <Spinner label="Loading conversation" />
              </div>
            ) : messages.length === 0 ? (
              <div className="mx-auto flex h-full max-w-md flex-col items-center justify-center text-center">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-surface-2 text-accent">
                  <Sparkles className="h-6 w-6" />
                </div>
                <h2 className="text-base font-semibold text-zinc-100">
                  {kbActive ? 'Ask the team knowledge base' : 'Ask the architecture assistant'}
                </h2>
                <p className="mt-1.5 text-sm text-zinc-500">
                  {kbActive
                    ? 'Answers are grounded in the team knowledge base and cite their sources. Conversations are saved automatically.'
                    : 'A plain-LLM architecture chat (no retrieval). Conversations are saved automatically.'}
                </p>
                <div className="mt-5 grid w-full gap-2 text-left">
                  {[
                    'Walk me through how a request flows through our services.',
                    'What are the trade-offs of our data model choices?',
                    'How should a new service integrate with our platform?',
                  ].map((example) => (
                    <button
                      key={example}
                      type="button"
                      onClick={() => void send(example)}
                      className="rounded-xl border border-surface-border bg-surface-2 px-3.5 py-2.5 text-sm text-zinc-300 transition-colors hover:border-accent/40 hover:bg-surface-3"
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="mx-auto flex max-w-3xl flex-col gap-4">
                {messages.map((m) => (
                  <div key={m.id} className="flex flex-col gap-2">
                    <MessageBubble message={m} />
                    {m.role === 'assistant' &&
                      !m.error &&
                      m.citations &&
                      m.citations.length > 0 && (
                        <div className="pl-11">
                          <CitationsPanel citations={m.citations} />
                        </div>
                      )}
                  </div>
                ))}
                {sending && (
                  <div className="flex items-center gap-2 pl-11 text-sm text-zinc-500">
                    <Spinner label="Assistant is typing" />
                    <span className="animate-fade-in">
                      {kbActive ? 'Retrieving & grounding…' : 'Thinking…'}
                    </span>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>
            )}
          </div>

          {/* Composer */}
          <Composer sending={sending} onSend={(text) => void send(text)} />
        </div>
      </div>
    </div>
  );
}

export default ChatPage;
